// hooks/useSourceSearch.js - Version corrigée pour éliminer la boucle infinie
import { useState, useEffect, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import sourceService from './SourceService';
import userPreferencesManager from '../components/UserPreferencesManager';
import GeoLanguageService from '../components/GeoLanguageService';

// Configuration des langues par pays (identique)
const COUNTRY_LANGUAGE_CONFIG = {
  'MA': { 
    primary: ['fr', 'ar'], 
    secondary: ['en'], 
    name: 'Maroc',
    domains: ['.ma', '.org.ma', '.gov.ma', '.ac.ma']
  },
  'FR': { 
    primary: ['fr'], 
    secondary: ['en'], 
    name: 'France',
    domains: ['.fr', '.gouv.fr', '.edu.fr', '.org']
  },
  'US': { 
    primary: ['en'], 
    secondary: ['es'], 
    name: 'États-Unis',
    domains: ['.com', '.gov', '.edu', '.org']
  },
  'CA': { 
    primary: ['en', 'fr'], 
    secondary: [], 
    name: 'Canada',
    domains: ['.ca', '.gc.ca', '.edu.ca']
  },
  'ES': { 
    primary: ['es'], 
    secondary: ['en'], 
    name: 'Espagne',
    domains: ['.es', '.gob.es', '.edu.es']
  },
  'DE': { 
    primary: ['de'], 
    secondary: ['en'], 
    name: 'Allemagne',
    domains: ['.de', '.gov.de', '.edu.de']
  },
  'IT': { 
    primary: ['it'], 
    secondary: ['en'], 
    name: 'Italie',
    domains: ['.it', '.gov.it', '.edu.it']
  },
  'UK': { 
    primary: ['en'], 
    secondary: [], 
    name: 'Royaume-Uni',
    domains: ['.uk', '.gov.uk', '.ac.uk']
  },
  'BR': { 
    primary: ['pt'], 
    secondary: ['en'], 
    name: 'Brésil',
    domains: ['.br', '.gov.br', '.edu.br']
  },
  'JP': { 
    primary: ['ja'], 
    secondary: ['en'], 
    name: 'Japon',
    domains: ['.jp', '.go.jp', '.ac.jp']
  },
  'CN': { 
    primary: ['zh'], 
    secondary: ['en'], 
    name: 'Chine',
    domains: ['.cn', '.gov.cn', '.edu.cn']
  }
};

/**
 * Hook personnalisé pour la gestion de la recherche de sources
 */
export const useSourceSearch = (competenceId, token, semanticAnalysis, suggestedQuery) => {
  const auth = getAuth();
  
  // États principaux
  const [competence, setCompetence] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [competenceError, setCompetenceError] = useState(null);
  const [sources, setSources] = useState([]);
  const [savedSources, setSavedSources] = useState([]);
  const [metadata, setMetadata] = useState({});
  const [loadingSaved, setLoadingSaved] = useState(true);
  const [providerRecommendations, setProviderRecommendations] = useState([]);
  
  // États pour les paramètres de recherche avec valeurs stables
  const [searchParams, setSearchParams] = useState(() => ({
    query: '',
    objective: '',
    languages: ['en'],
    country: '',
    maxSources: 20,
    selectedProviders: [],
    providerMode: 'auto',
    preferredTypes: null,
    useAI: true
  }));

  // États pour l'analyse géographique
  const [geographicAnalysis, setGeographicAnalysis] = useState(null);
  const [autoConfigApplied, setAutoConfigApplied] = useState(null);
  const [geoAnalysisLoading, setGeoAnalysisLoading] = useState(false);

  // États pour les préférences utilisateur
  const [userPreferences, setUserPreferences] = useState(null);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  // NOUVEAU: Références pour éviter les boucles
  const competenceInitialized = useRef(false);
  const lastCompetenceId = useRef(null);
  const lastSuggestedQuery = useRef(null);
  const geoAnalysisCompleted = useRef(false);

  /**
   * Extrait les types de sources préférés depuis l'analyse sémantique
   */
  const extractPreferredTypesFromAnalysis = (analysis) => {
    if (!analysis || !analysis.contentTypes || !analysis.contentTypes.length) {
      return null;
    }
    
    const mappedTypes = [];
    
    if (analysis.contentTypes.includes('academic')) {
      mappedTypes.push('Educational');
    }
    if (analysis.contentTypes.includes('factual') || analysis.contentTypes.includes('reference')) {
      mappedTypes.push('Governmental');
    }
    if (analysis.contentTypes.includes('commercial')) {
      mappedTypes.push('Commercial');
    }
    if (analysis.contentTypes.includes('opinion') || analysis.contentTypes.includes('news')) {
      mappedTypes.push('Media');
    }
    if (analysis.contentTypes.includes('social')) {
      mappedTypes.push('Non-profit');
    }
    
    return mappedTypes.length > 0 ? mappedTypes : null;
  };

  /**
   * Charge les préférences utilisateur
   */
  const loadUserPreferences = async () => {
    if (!auth.currentUser || preferencesLoaded) return;

    try {
      console.log('Chargement des préférences utilisateur...');
      const preferences = await userPreferencesManager.getUserPreferences(auth.currentUser.uid);
      
      console.log('Préférences utilisateur chargées:', preferences);
      setUserPreferences(preferences);
      
      // Appliquer immédiatement les préférences aux paramètres de recherche
      setSearchParams(prev => ({
        ...prev,
        languages: preferences.language ? [preferences.language] : prev.languages,
        country: preferences.country || prev.country,
        maxSources: preferences.search?.maxSources || prev.maxSources,
        useAI: preferences.search?.useAI !== false,
        preferredTypes: preferences.search?.preferredTypes || prev.preferredTypes
      }));

      setPreferencesLoaded(true);
      console.log('Préférences appliquées aux paramètres de recherche');
      
    } catch (error) {
      console.error('Erreur lors du chargement des préférences:', error);
      
      const defaultPrefs = userPreferencesManager.getDefaultPreferences();
      setUserPreferences(defaultPrefs);
      
      setSearchParams(prev => ({
        ...prev,
        languages: [defaultPrefs.language],
        country: defaultPrefs.country
      }));
      
      setPreferencesLoaded(true);
    }
  };

  /**
   * Analyse le contexte géographique d'une requête
   * Utilise GeoLanguageService.detectGeographicContext pour une détection complète
   */
  const analyzeGeographicContext = async (query, objective) => {
    if (!query && !objective) return null;
    
    setGeoAnalysisLoading(true);
    
    try {
      const textToAnalyze = `${query} ${objective}`;
      
      console.log('🌍 Analyse géographique avec GeoLanguageService pour:', textToAnalyze);
      
      // Utiliser la fonction complète de GeoLanguageService qui détecte 70+ pays et villes
      const detection = GeoLanguageService.detectGeographicContext(textToAnalyze);
      
      if (detection && detection.hasGeographicContext) {
        console.log('✅ Contexte géographique détecté:', detection.countries);
        
        // Enrichir avec les domaines depuis COUNTRY_LANGUAGE_CONFIG
        const enrichedCountries = detection.countries.map(country => {
          const config = COUNTRY_LANGUAGE_CONFIG[country.code];
          return {
            ...country,
            domains: config ? config.domains : GeoLanguageService.getCountryDomains(country.code)
          };
        });
        
        return {
          hasGeographicContext: true,
          countries: enrichedCountries
        };
      }
      
      console.log('ℹ️ Aucun contexte géographique détecté');
      return {
        hasGeographicContext: false,
        countries: []
      };

    } catch (error) {
      console.error('❌ Erreur lors de l\'analyse géographique:', error);
      return null;
    } finally {
      setGeoAnalysisLoading(false);
    }
  };

  /**
   * Applique automatiquement la configuration géographique
   */
  const applyGeographicConfiguration = (analysis, force = false) => {
    if (!analysis || !analysis.hasGeographicContext || analysis.countries.length === 0) {
      return false;
    }

    if (autoConfigApplied && !force) {
      return false;
    }

    const detectedCountry = analysis.countries[0];
    const countryConfig = COUNTRY_LANGUAGE_CONFIG[detectedCountry.code];

    if (!countryConfig) {
      console.warn(`Configuration non trouvée pour le pays: ${detectedCountry.code}`);
      return false;
    }

    const optimizedLanguages = [
      ...countryConfig.primary,
      ...countryConfig.secondary
    ].filter((lang, index, arr) => arr.indexOf(lang) === index);

    console.log('Configuration géographique remplace les préférences utilisateur');

    setSearchParams(prev => ({
      ...prev,
      country: detectedCountry.code,
      languages: optimizedLanguages,
      enableGeographicLocalization: true,
      localGlobalRatio: 0.8
    }));

    const appliedConfig = {
      country: {
        code: detectedCountry.code,
        name: countryConfig.name
      },
      languages: optimizedLanguages,
      domains: countryConfig.domains,
      appliedAt: new Date().toISOString()
    };

    setAutoConfigApplied(appliedConfig);
    console.log('Configuration géographique appliquée:', appliedConfig);
    return true;
  };

  /**
   * Déclenche l'analyse géographique manuellement
   */
  const handleApplyGeographicConfig = () => {
    if (geographicAnalysis) {
      applyGeographicConfiguration(geographicAnalysis, true);
    }
  };

  /**
   * Récupère les données de compétence - VERSION CORRIGÉE
   */
  const fetchCompetence = async () => {
    if (!competenceId || !auth.currentUser) {
      if (!competenceId) {
        setCompetenceError('ID de compétence manquant');
      }
      return null;
    }
    
    // CORRECTION: Éviter les appels redondants
    if (lastCompetenceId.current === competenceId && competenceInitialized.current) {
      console.log('Compétence déjà chargée, éviter le rechargement');
      return competence;
    }
    
    setCompetenceError(null);
    setLoading(true);
    
    try {
      console.log('Récupération de la compétence avec ID:', competenceId);
      console.log('UID utilisateur:', auth.currentUser.uid);
      
      const competenceDocRef = doc(db, 'users', auth.currentUser.uid, 'competences', competenceId);
      const competenceDocSnap = await getDoc(competenceDocRef);
      
      if (competenceDocSnap.exists()) {
        console.log('Compétence trouvée:', competenceDocSnap.data());
        const competenceData = { id: competenceId, ...competenceDocSnap.data() };
        setCompetence(competenceData);
        
        // CORRECTION: Mettre à jour les searchParams séparément, sans redéclencher l'effet
        setSearchParams(prev => ({
          ...prev,
          objective: competenceData.objective || competenceData.title || prev.objective
          // Retirer query d'ici pour éviter les conflits avec suggestedQuery
        }));
        
        // Marquer comme initialisé
        competenceInitialized.current = true;
        lastCompetenceId.current = competenceId;
        
        setError(null);
        return competenceData;
      } else {
        console.error('Compétence non trouvée avec ID:', competenceId);
        setCompetenceError(`Compétence introuvable (ID: ${competenceId}). Vérifiez que vous avez sélectionné une compétence valide.`);
        setCompetence(null);
        return null;
      }
    } catch (err) {
      console.error('Erreur lors de la récupération de la compétence:', err);
      setCompetenceError(`Impossible de récupérer les données de l\'objectif: ${err.message}`);
      setCompetence(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // ... (autres fonctions inchangées: fetchSavedSources, handleSearch, etc.)
  const fetchSavedSources = async () => {
    if (!token || !auth.currentUser || !competenceId) return;
    
    setLoadingSaved(true);
    
    try {
      console.log("Récupération des sources pour:", auth.currentUser.uid);
      
      const result = await sourceService.getSavedSources(
        auth.currentUser.uid, 
        token, 
        { competenceId }
      );
      
      if (result && result.success && Array.isArray(result.sources) && result.sources.length > 0) {
        const sortedSessions = [...result.sources].sort((a, b) => {
          const dateA = new Date(a.createdAt || a.updatedAt || 0);
          const dateB = new Date(b.createdAt || b.updatedAt || 0);
          return dateB - dateA;
        });
        
        const latestSession = sortedSessions[0];
        
        if (latestSession.sources && Array.isArray(latestSession.sources)) {
          const formattedSources = latestSession.sources.map(source => ({
            ...source,
            sessionId: latestSession.id,
            title: source.title || source.domain || 'Source sans titre',
            description: source.description || 'Aucune description disponible',
            url: source.url || `https://${source.domain}` || '#',
            domain: source.domain || 'Source externe',
            type: source.type || 'Unknown',
            reliability: source.reliability || 'Medium',
            relevanceScore: source.relevanceScore || 5,
            popularityScore: source.popularityScore || 5,
            usableUrl: source.usableUrl || source.recommendedUrl || source.url || `https://${source.domain}`,
            recommendedUrl: source.recommendedUrl || source.url || `https://${source.domain}`,
            originalUrl: source.originalUrl || source.url || `https://${source.domain}`,
            hasUrlFallback: source.hasUrlFallback || source.fallbackApplied || false,
            accessibilityStatus: source.accessibilityStatus || 'not_verified',
            accessibilityInfo: source.accessibilityInfo || {},
            fallbackApplied: source.fallbackApplied || false,
            userMessage: source.userMessage || null
          }));

          setSavedSources(formattedSources);
          
          setMetadata({
            title: `Sources pour: "${latestSession.query}"`,
            category: latestSession.category || "Non spécifiée",
            count: formattedSources.length,
            timestamp: latestSession.createdAt || latestSession.updatedAt,
            sessionId: latestSession.id,
            sourceId: latestSession.id,
            recovery: latestSession.recovery || null,
            accessibility: latestSession.accessibility || null
          });
          
        } else {
          setSavedSources([]);
          setMetadata({ title: "Aucune source sauvegardée", category: "Non spécifiée" });
        }
      } else {
        setSavedSources([]);
        setMetadata({ title: "Aucune source sauvegardée", category: "Non spécifiée" });
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des sources:', err);
      setError('Impossible de récupérer les sources sauvegardées');
      setSavedSources([]);
    } finally {
      setLoadingSaved(false);
    }
  };

  const handleSearch = async (params) => {
    console.log("handleSearch appelé avec les paramètres:", params);
    
    if (!token || !auth.currentUser) {
      setError('Vous devez être connecté pour effectuer une recherche.');
      return;
    }
    
    if (!competenceId) {
      setError('Veuillez d\'abord sélectionner une objectif pour effectuer une recherche ciblée.');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSources([]);
    setMetadata({});
    
    try {
      setSearchParams(params);
      
      const searchRequest = {
        userId: auth.currentUser.uid,
        competenceId,
        userObjective: competence?.title || params.objective,
        query: params.query || params.objective,
        languages: params.languages || searchParams.languages,
        country: params.country || searchParams.country,
        maxSources: params.maxSources || searchParams.maxSources,
        selectedProviders: params.selectedProviders,
        preferredSourceTypes: params.preferredTypes,
        useAI: params.useAI !== undefined ? params.useAI : searchParams.useAI,
        semanticAnalysis: params.semanticAnalysis || semanticAnalysis,
        fromSuggestedQuery: params.fromSuggestedQuery || false,
        enableGeographicLocalization: params.enableGeographicLocalization !== undefined ? 
          params.enableGeographicLocalization : (userPreferences?.enableGeographicLocalization !== false),
        localGlobalRatio: params.localGlobalRatio || userPreferences?.localGlobalRatio || 0.7,
        geographicAnalysis: geographicAnalysis,
        userPreferences: userPreferences
      };
      
      const result = await sourceService.getRecommendedSources(searchRequest, token);
      
      if (result && result.sources) {
        setSources(result.sources);
        setMetadata(result.metadata || {});
        
        if (result.metadata && result.metadata.providers) {
          setProviderRecommendations(result.metadata.providers);
        }
        
        return { success: true, sources: result.sources, metadata: result.metadata };
      } else {
        setError('Aucun résultat trouvé. Veuillez modifier votre recherche.');
        return { success: false, error: 'Aucun résultat trouvé' };
      } 
    } catch (err) {
      console.error('Erreur lors de la recherche de sources:', err);
      const errorMessage = err.message || 'Erreur lors de la recherche. Veuillez réessayer.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const handleModeChange = async (newMode) => {
    console.log("Mode changé vers:", newMode);
    
    setSearchParams(prev => ({...prev, providerMode: newMode}));
    
    if (newMode === 'guided' && searchParams.objective) {
      try {
        const providers = await sourceService.getRecommendedProviders(
          searchParams.query, 
          searchParams.objective,
          token
        );
        
        if (providers && providers.length > 0) {
          setProviderRecommendations(providers);
          return providers;
        }
      } catch (err) {
        console.error("Erreur lors du chargement des recommandations:", err);
      }
    }
    return [];
  };

  const handleDeleteSavedSource = async (source) => {
    if (!source) {
      setError("Impossible de supprimer cette source: source invalide");
      return;
    }
    
    try {
      const sourceIdentifier = source.url || `${source.domain}-${source.title}`;
      
      const updatedSources = savedSources.filter(s => {
        const sIdentifier = s.url || `${s.domain}-${s.title}`;
        return sIdentifier !== sourceIdentifier;
      });
      
      setSavedSources(updatedSources);
      
      setMetadata(prev => ({
        ...prev,
        count: updatedSources.length,
        title: updatedSources.length === 0 ? "Aucune source sauvegardée" : prev.title,
        isModified: true,
        lastModified: new Date().toISOString()
      }));
      
    } catch (err) {
      console.error("Erreur lors de la suppression locale:", err);
      setError("Erreur lors de la suppression locale de la source");
    }
  };


  // 1. Charger les préférences utilisateur UNE SEULE FOIS
  useEffect(() => {
    if (auth.currentUser && !preferencesLoaded) {
      loadUserPreferences();
    }
  }, [auth.currentUser]); // Supprimé preferencesLoaded des deps

  // 2. Charger la compétence UNE SEULE FOIS par competenceId
  useEffect(() => {
    if (competenceId && auth.currentUser && token && !competenceInitialized.current) {
      fetchCompetence();
    }
  }, [competenceId, auth.currentUser, token]); // Supprimé suggestedQuery

  // 3. Appliquer suggestedQuery à la compétence SÉPARÉMENT
  useEffect(() => {
    if (suggestedQuery && suggestedQuery !== lastSuggestedQuery.current && competence) {
      console.log('Application de la requête suggérée:', suggestedQuery);
      setSearchParams(prev => ({
        ...prev,
        query: suggestedQuery
      }));
      lastSuggestedQuery.current = suggestedQuery;
    }
  }, [suggestedQuery, competence]); // Effet séparé pour suggestedQuery

  // 4. Analyse géographique UNE SEULE FOIS quand les données sont prêtes
  useEffect(() => {
    const performGeographicAnalysis = async () => {
      if (!competence || !preferencesLoaded || geoAnalysisCompleted.current) return;

      const queryText = suggestedQuery || searchParams.query || '';
      const objectiveText = competence.title || competence.objective || '';

      if (!queryText && !objectiveText) return;

      console.log('Analyse géographique automatique en cours...');
      const analysis = await analyzeGeographicContext(queryText, objectiveText);
      
      if (analysis) {
        setGeographicAnalysis(analysis);
        
        if (analysis.hasGeographicContext) {
          const applied = applyGeographicConfiguration(analysis);
          if (applied) {
            console.log('Configuration géographique appliquée automatiquement');
          }
        }
        
        geoAnalysisCompleted.current = true; // Marquer comme terminé
      }
    };

    performGeographicAnalysis();
  }, [competence?.id, preferencesLoaded]); // Dépendances minimales et stables

  // 5. Récupérer les sources sauvegardées
  useEffect(() => {
    if (token && competenceId) {
      fetchSavedSources();
    }
  }, [token, competenceId]);

  // 6. Recherche automatique avec requête suggérée - AVEC GARDE
  useEffect(() => {
    if (suggestedQuery && 
        competence && 
        token && 
        semanticAnalysis && 
        !sources.length && 
        preferencesLoaded &&
        suggestedQuery === lastSuggestedQuery.current) { // Garde supplémentaire
      
      console.log("Déclenchement de la recherche automatique avec requête suggérée");
      
      const timer = setTimeout(() => {
        const autoSearchParams = {
          query: suggestedQuery,
          objective: competence.title || competence.objective || '',
          languages: searchParams.languages,
          country: searchParams.country,
          maxSources: searchParams.maxSources,
          selectedProviders: [],
          providerMode: 'auto', 
          preferredTypes: extractPreferredTypesFromAnalysis(semanticAnalysis),
          useAI: searchParams.useAI,
          competenceId,
          fromSuggestedQuery: true,
          semanticAnalysis: semanticAnalysis,
          autoTriggered: true,
          enableGeographicLocalization: userPreferences?.enableGeographicLocalization !== false,
          localGlobalRatio: userPreferences?.localGlobalRatio || 0.7
        };
        
        handleSearch(autoSearchParams);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [suggestedQuery, competence?.id, token, semanticAnalysis?.intention, preferencesLoaded]);

  return {
    // États
    competence,
    loading,
    error,
    competenceError,
    sources,
    savedSources,
    metadata,
    loadingSaved,
    searchParams,
    geographicAnalysis,
    autoConfigApplied,
    geoAnalysisLoading,
    userPreferences,
    preferencesLoaded,
    providerRecommendations,
    
    // Fonctions
    handleSearch,
    handleModeChange,
    handleDeleteSavedSource,
    handleApplyGeographicConfig,
    fetchSavedSources,
    fetchCompetence,
    loadUserPreferences,
    extractPreferredTypesFromAnalysis,
    setSearchParams,
    setSources,
    setMetadata,
    setProviderRecommendations,
    setError
  };
};
